/*
 * Markdown in, message document out: the tokenizer a message is read with, and the token map that
 * turns its tokens into nodes. markdown-it is configured down to the dialect the server accepts —
 * no setext headings, no indented code, no inline HTML, linkify only where goldmark linkifies — and
 * the token map below is *total*: a token with no row throws rather than being dropped, so a
 * construct this editor cannot represent fails loudly in a test instead of silently eating a
 * message.
 *
 * The one place the dialect differs from CommonMark is the single newline: in a chat message it is
 * a line break, and the token map says so by mapping `softbreak` to `hard_break`. Everything else
 * follows the corpus under internal/core/markdown/testdata/markdown/, which the round-trip
 * gate replays through both parsers.
 */

import type Token from "markdown-it/lib/token.mjs"
import { taskListRule, type ParseSpec, type TokenizerConfig } from "../dialect/index.ts"
import { allowedHref, LANG_SOURCE } from "./schema.ts"

/** The info string a fence may carry into the document; the dialect spells it once, in the schema. */
const LANG = new RegExp(`^${LANG_SOURCE}$`)

/**
 * A tight list is one whose items markdown-it marked hidden — the same "no blank line between the
 * items" rule the server reads off goldmark's List.IsTight.
 */
function listIsTight(tokens: Token[], i: number): boolean {
	for (let j = i + 1; j < tokens.length; j++) {
		const tok = tokens[j]
		if (tok === undefined) break
		if (tok.type !== "list_item_open") return tok.hidden
	}
	return false
}

/** The alignment markdown-it writes onto a table cell as a style attribute. */
function alignOf(tok: Token): string {
	const style = tok.attrGet("style") ?? ""
	const found = /text-align:\s*(left|center|right)/.exec(style)
	return found?.[1] ?? ""
}

/**
 * The tokenizer's configuration. Every option here is a decision the server already made:
 * `html: false` keeps a tag literal text, `linkify` catches what goldmark's linkifier catches, and
 * `breaks: false` leaves the token stream CommonMark's — the dialect's newline rule lives in the
 * token map, which is the one place that states it. The task-list pre-pass is the builder's, and
 * every dialect of this product runs it.
 */
export const messageTokenizer: TokenizerConfig = {
	options: { html: false, linkify: true, breaks: false, typographer: false },
	disable: ["lheading", "code"],
	// goldmark's linkifier needs a scheme (parser.go configures http and https alone) and does catch
	// a bare address; markdown-it's fuzzy link would turn "example.com" into a link the server would
	// leave as text, which is a lie about what was sent.
	linkify: {
		set: { fuzzyLink: false, fuzzyIP: false, fuzzyEmail: true },
		add: [
			["mailto:", null],
			["//", null],
		],
	},
	validateLink: allowedHref,
	coreRules: [taskListRule],
}

/**
 * The token map. One row per markdown-it token this dialect can produce; the parser throws on any
 * other, which is what makes the map total.
 */
export const TOKENS: Record<string, ParseSpec> = {
	paragraph: { block: "paragraph" },
	heading: { block: "heading", getAttrs: (tok) => ({ level: Number(tok.tag.slice(1)) || 1 }) },
	blockquote: { block: "blockquote" },
	fence: {
		block: "code_block",
		getAttrs: (tok) => {
			const params = tok.info.trim().split(/\s+/)[0]?.toLowerCase() ?? ""
			return { params: LANG.test(params) ? params : "" }
		},
		noCloseToken: true,
	},
	bullet_list: {
		block: "bullet_list",
		getAttrs: (_, tokens, i) => ({ tight: listIsTight(tokens, i) }),
	},
	ordered_list: {
		block: "ordered_list",
		getAttrs: (tok, tokens, i) => ({
			order: Number(tok.attrGet("start")) || 1,
			tight: listIsTight(tokens, i),
		}),
	},
	list_item: {
		block: "list_item",
		getAttrs: (tok) => ({ checked: (tok.meta as { checked?: boolean } | null)?.checked ?? null }),
	},
	table: { block: "table" },
	thead: { ignore: true },
	tbody: { ignore: true },
	tr: { block: "table_row" },
	th: { block: "table_cell", getAttrs: (tok) => ({ header: true, align: alignOf(tok) }) },
	td: { block: "table_cell", getAttrs: (tok) => ({ header: false, align: alignOf(tok) }) },
	hr: { node: "horizontal_rule" },
	image: {
		node: "image",
		getAttrs: (tok) => ({
			src: tok.attrGet("src") ?? "",
			alt: tok.children?.[0]?.content ?? "",
			title: tok.attrGet("title") ?? "",
		}),
	},
	// The dialect's newline rule: one newline in a chat message is a line break, not a space.
	softbreak: { node: "hard_break" },
	hardbreak: { node: "hard_break" },
	em: { mark: "em" },
	strong: { mark: "strong" },
	s: { mark: "strike" },
	code_inline: { mark: "code", noCloseToken: true },
	link: {
		mark: "link",
		getAttrs: (tok) => ({
			href: tok.attrGet("href") ?? "",
			title: tok.attrGet("title") ?? "",
			auto: tok.markup === "linkify" || tok.markup === "autolink",
		}),
	},
}
